/**
 * Admin notification utilities.
 * Writes to RTDB notifications/ and provides API helpers for reads/updates.
 */
import { getDatabase, ref, push, set, update, get } from "firebase/database";
import { getAuth } from "firebase/auth";

const serverUrl = process.env.REACT_APP_BASE_URL;

export function isNotificationUnread(notification) {
  return notification?.read !== true;
}

export const NOTIFICATION_TYPES = {
  NEW_RENTER: "NEW_RENTER",
  NEW_BOAT_OWNER: "NEW_BOAT_OWNER",
  NEW_BOAT: "NEW_BOAT",
  LISTING_MODIFIED: "LISTING_MODIFIED",
  VERIFICATION_SUBMITTED: "VERIFICATION_SUBMITTED",
  NEW_BOOKING: "NEW_BOOKING",
  PAYMENT_PAID: "PAYMENT_PAID",
  TRIP_ENDED: "TRIP_ENDED",
  WAIVER_COMPLETED: "WAIVER_COMPLETED",
  WITHDRAWAL_REQUESTED: "WITHDRAWAL_REQUESTED",
};

async function getAuthHeaders() {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error("Not authenticated");
  }
  const token = await user.getIdToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

export async function createAdminNotification({ type, title, message, link }) {
  const db = getDatabase();
  const newRef = push(ref(db, "notifications"));
  const data = {
    type,
    title,
    message,
    read: false,
    createdAt: Date.now(),
    link: link || "/dashboard",
  };

  await set(newRef, data);
  return { id: newRef.key, ...data };
}

export function notifyNewRenter(name) {
  return createAdminNotification({
    type: NOTIFICATION_TYPES.NEW_RENTER,
    title: "New Renter",
    message: name
      ? `${name} has registered as a new renter.`
      : "A new renter has registered.",
    link: "/dashboard/users",
  });
}

export function notifyNewBoatOwner(name) {
  return createAdminNotification({
    type: NOTIFICATION_TYPES.NEW_BOAT_OWNER,
    title: "New Boat Owner",
    message: name
      ? `${name} has registered as a new boat owner.`
      : "A new boat owner has registered.",
    link: "/dashboard/boat-owners",
  });
}

export function notifyNewBoat(listingTitle, listingId) {
  const link = listingId
    ? `/dashboard/pending-listings`
    : "/dashboard/pending-listings";
  return createAdminNotification({
    type: NOTIFICATION_TYPES.NEW_BOAT,
    title: "New Boat",
    message: listingTitle
      ? `"${listingTitle}" has been added and is pending review.`
      : "A new boat has been added.",
    link,
  });
}

export function notifyListingModified(listingTitle, listingId) {
  const link = listingId
    ? `/dashboard/listing/${listingId}`
    : "/dashboard/pending-listings";
  return createAdminNotification({
    type: NOTIFICATION_TYPES.LISTING_MODIFIED,
    title: "Listing Updated",
    message: listingTitle
      ? `"${listingTitle}" was updated and is pending your approval.`
      : "A boat listing was updated and is pending approval.",
    link,
  });
}

/** Active/rejected listings go back to Pending when the owner edits. */
export function getBoatStatusAfterOwnerEdit(currentStatus) {
  if (currentStatus === "Active" || currentStatus === "Reject") {
    return "Pending";
  }
  return currentStatus || "Pending";
}

export function notifyVerificationSubmitted(userName, userId, boatType = "Bass Boat") {
  const link = userId
    ? `/dashboard/user/${userId}`
    : "/dashboard/pending-renters";
  return createAdminNotification({
    type: NOTIFICATION_TYPES.VERIFICATION_SUBMITTED,
    title: "Verification Submitted",
    message: userName
      ? `${userName} submitted a ${boatType} verification application for review.`
      : `A new ${boatType} verification application was submitted for review.`,
    link,
  });
}

export function notifyNewBooking(listingTitle, bookingId) {
  return createAdminNotification({
    type: NOTIFICATION_TYPES.NEW_BOOKING,
    title: "New Booking",
    message: listingTitle
      ? `A new booking was created for "${listingTitle}".`
      : "A new booking has been created.",
    link: "/dashboard/bookings",
  });
}

export function notifyTripEnded(listingTitle, bookingId) {
  return createAdminNotification({
    type: NOTIFICATION_TYPES.TRIP_ENDED,
    title: "Trip Ended",
    message: listingTitle
      ? `The trip for "${listingTitle}" has ended.`
      : "A trip has ended.",
    link: bookingId ? `/dashboard/event/${bookingId}` : "/dashboard/bookings",
  });
}

export function notifyWithdrawalRequested(ownerName, amountDollars) {
  const formattedAmount = Number(amountDollars).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return createAdminNotification({
    type: NOTIFICATION_TYPES.WITHDRAWAL_REQUESTED,
    title: "Withdrawal Requested",
    message: ownerName
      ? `${ownerName} requested a withdrawal of $${formattedAmount}.`
      : `A boat owner requested a withdrawal of $${formattedAmount}.`,
    link: "/dashboard/withdrawals",
  });
}

export async function fetchAdminNotifications() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${serverUrl}/api/admin/notifications`, { headers });
  if (!res.ok) throw new Error("Failed to fetch notifications");
  return res.json();
}

export async function fetchUnreadNotificationCount() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${serverUrl}/api/admin/notifications/unread-count`, {
    headers,
  });
  if (!res.ok) throw new Error("Failed to fetch unread count");
  return res.json();
}

export async function markNotificationRead(id) {
  const db = getDatabase();
  const notificationRef = ref(db, `notifications/${id}`);
  const snapshot = await get(notificationRef);

  if (!snapshot.exists()) {
    throw new Error("Notification not found");
  }

  await update(notificationRef, { read: true });
  return { success: true };
}

export async function markAllNotificationsRead() {
  const db = getDatabase();
  const notificationsRef = ref(db, "notifications");
  const snapshot = await get(notificationsRef);
  const updates = {};

  if (snapshot.exists()) {
    snapshot.forEach((child) => {
      if (child.val().read !== true) {
        updates[`notifications/${child.key}/read`] = true;
      }
    });
  }

  if (Object.keys(updates).length > 0) {
    await update(ref(db), updates);
  }

  return { success: true };
}

export function getNotificationNavigationLink(notification) {
  if (notification?.type === NOTIFICATION_TYPES.NEW_RENTER) {
    return "/dashboard/users";
  }

  return notification?.link || null;
}

export function getNotificationIcon(type) {
  switch (type) {
    case NOTIFICATION_TYPES.NEW_RENTER:
      return "🟢";
    case NOTIFICATION_TYPES.NEW_BOAT_OWNER:
      return "👤";
    case NOTIFICATION_TYPES.NEW_BOAT:
      return "🚤";
    case NOTIFICATION_TYPES.LISTING_MODIFIED:
      return "✏️";
    case NOTIFICATION_TYPES.VERIFICATION_SUBMITTED:
      return "📋";
    case NOTIFICATION_TYPES.NEW_BOOKING:
      return "📅";
    case NOTIFICATION_TYPES.PAYMENT_PAID:
      return "💳";
    case NOTIFICATION_TYPES.TRIP_ENDED:
      return "🏁";
    case NOTIFICATION_TYPES.WAIVER_COMPLETED:
      return "📝";
    case NOTIFICATION_TYPES.WITHDRAWAL_REQUESTED:
      return "💸";
    default:
      return "🔔";
  }
}

export function formatTimeAgo(timestamp) {
  if (!timestamp) return "";
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(timestamp).toLocaleDateString();
}
